"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import { getAnalyticsConsentCopy } from "@/content/analytics/consent";
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  type AnalyticsConsent,
  parseAnalyticsConsent,
  persistAnalyticsConsent,
} from "@/utils/analyticsConsent";
import {
  getAnalyticsPageGroup,
  getSanitizedAnalyticsPath,
  isPublicAnalyticsPath,
} from "@/utils/analyticsPrivacy";
import { getLocalizedPath } from "@/utils/utils";

type AnalyticsManagerProps = {
  locale: string;
  domain: string;
  googleAnalyticsId: string;
  clarityProjectId: string;
};

type ClarityFunction = {
  (..._args: unknown[]): void;
  q?: unknown[][];
};

type AnalyticsWindow = Window & {
  dataLayer?: unknown[][];
  clarity?: ClarityFunction;
};

const GOOGLE_SCRIPT_ID = "ariaflow-google-analytics";
const CLARITY_SCRIPT_ID = "ariaflow-clarity";

const setGoogleAnalyticsDisabled = (
  analyticsWindow: AnalyticsWindow,
  measurementId: string,
  disabled: boolean,
) => {
  const windowFlags = analyticsWindow as Window &
    Record<string, boolean | undefined>;
  windowFlags[`ga-disable-${measurementId}`] = disabled;
};

const isProductionDomain = (domain: string) => {
  const hostname = window.location.hostname.toLowerCase();
  const normalizedDomain = domain.toLowerCase();
  return (
    process.env.NODE_ENV === "production" &&
    (hostname === normalizedDomain || hostname.endsWith(`.${normalizedDomain}`))
  );
};

const ensureGoogleAnalytics = (
  analyticsWindow: AnalyticsWindow,
  measurementId: string,
) => {
  if (!measurementId) return;

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.gtag =
    analyticsWindow.gtag ||
    function gtag(...args: unknown[]) {
      analyticsWindow.dataLayer?.push(args);
    };

  if (!document.getElementById(GOOGLE_SCRIPT_ID)) {
    analyticsWindow.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });
    analyticsWindow.gtag("js", new Date());
    analyticsWindow.gtag("config", measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      measurementId,
    )}`;
    document.head.appendChild(script);
  }
};

const updateGoogleAnalyticsConsent = (
  analyticsWindow: AnalyticsWindow,
  measurementId: string,
  consent: AnalyticsConsent | null,
) => {
  if (!measurementId) return;

  analyticsWindow.gtag?.("consent", "update", {
    analytics_storage: consent === "granted" ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  // Keep the tag enabled in denied mode so Consent Mode can emit only its
  // limited cookieless measurements. Storage remains denied above.
  setGoogleAnalyticsDisabled(analyticsWindow, measurementId, false);
};

const ensureClarity = (
  analyticsWindow: AnalyticsWindow,
  projectId: string,
) => {
  if (!projectId) return;

  analyticsWindow.clarity =
    analyticsWindow.clarity ||
    Object.assign(
      (...args: unknown[]) => {
        analyticsWindow.clarity?.q?.push(args);
      },
      { q: [] as unknown[][] },
    );

  if (!document.getElementById(CLARITY_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = CLARITY_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
    document.head.appendChild(script);
  }
};

const updateClarityConsent = (
  analyticsWindow: AnalyticsWindow,
  consent: AnalyticsConsent | null,
) => {
  analyticsWindow.clarity?.("consentv2", {
    ad_Storage: "denied",
    analytics_Storage: consent === "granted" ? "granted" : "denied",
  });
};

const disableOptionalAnalytics = (
  analyticsWindow: AnalyticsWindow,
  measurementId: string,
) => {
  if (measurementId) {
    setGoogleAnalyticsDisabled(analyticsWindow, measurementId, true);
  }
  analyticsWindow.gtag?.("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  analyticsWindow.clarity?.("consentv2", {
    ad_Storage: "denied",
    analytics_Storage: "denied",
  });
  analyticsWindow.clarity?.("consent", false);
};

export default function AnalyticsManager({
  locale,
  domain,
  googleAnalyticsId,
  clarityProjectId,
}: AnalyticsManagerProps) {
  const pathname = usePathname();
  const copy = getAnalyticsConsentCopy(locale);
  const [consent, setConsent] = useState<AnalyticsConsent | null>(null);
  const [consentResolved, setConsentResolved] = useState(false);
  const lastPageView = useRef<string | null>(null);

  useEffect(() => {
    const syncConsent = () => {
      setConsent(parseAnalyticsConsent(document.cookie));
      setConsentResolved(true);
    };

    syncConsent();
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, syncConsent);

    return () => {
      window.removeEventListener(
        ANALYTICS_CONSENT_CHANGED_EVENT,
        syncConsent,
      );
    };
  }, []);

  useEffect(() => {
    const analyticsWindow = window as AnalyticsWindow;
    const sanitizedPath = getSanitizedAnalyticsPath(pathname);
    const canLoad = Boolean(sanitizedPath) && isProductionDomain(domain);

    if (!canLoad || !sanitizedPath) {
      document.documentElement.setAttribute("data-clarity-mask", "true");
      disableOptionalAnalytics(analyticsWindow, googleAnalyticsId);
      lastPageView.current = null;
      return;
    }

    if (consent === "granted") {
      document.documentElement.removeAttribute("data-clarity-mask");
    } else {
      document.documentElement.setAttribute("data-clarity-mask", "true");
    }

    ensureGoogleAnalytics(analyticsWindow, googleAnalyticsId);
    updateGoogleAnalyticsConsent(
      analyticsWindow,
      googleAnalyticsId,
      consent,
    );
    ensureClarity(analyticsWindow, clarityProjectId);
    updateClarityConsent(analyticsWindow, consent);

    if (lastPageView.current === sanitizedPath) return;
    lastPageView.current = sanitizedPath;

    const pageUrl = `${window.location.origin}${sanitizedPath}`;
    const pageGroup = getAnalyticsPageGroup(pathname) || "public";

    analyticsWindow.gtag?.("event", "page_view", {
      page_path: sanitizedPath,
      page_location: pageUrl,
      page_title: document.title,
      page_group: pageGroup,
      page_locale: locale,
    });
  }, [
    clarityProjectId,
    consent,
    domain,
    googleAnalyticsId,
    locale,
    pathname,
  ]);

  const saveConsent = (nextConsent: AnalyticsConsent) => {
    persistAnalyticsConsent(
      nextConsent,
      window.location.protocol === "https:",
    );
    setConsent(nextConsent);
  };

  const isPublicPage = isPublicAnalyticsPath(pathname);
  const shouldShowBanner =
    consentResolved && isPublicPage && consent === null;

  return (
    <>
      {shouldShowBanner && (
        <section
          aria-label={copy.title}
          aria-live="polite"
          className="fixed inset-x-3 bottom-3 z-[100] rounded-xl border border-zinc-200 bg-white p-4 text-zinc-950 shadow-[0_18px_48px_rgba(24,24,27,0.2)] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-[25rem]"
        >
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-sm font-bold">{copy.title}</h2>
            <a
              href={getLocalizedPath("/privacy-policy", locale)}
              className="shrink-0 text-xs font-semibold text-violet-700 hover:text-violet-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              {copy.privacy}
            </a>
          </div>
          <p className="mt-1 text-[13px] leading-5 text-zinc-600">
            {copy.description}
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,.8fr)_minmax(0,1.45fr)]">
            <button
              type="button"
              onClick={() => saveConsent("granted")}
              className="inline-flex min-h-9 items-center justify-center rounded-lg bg-violet-700 px-3 py-1.5 text-center text-xs font-semibold leading-4 text-white hover:bg-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              {copy.accept}
            </button>
            <button
              type="button"
              onClick={() => saveConsent("denied")}
              className="inline-flex min-h-9 items-center justify-center rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-center text-xs font-semibold leading-4 text-zinc-700 hover:border-zinc-400 hover:text-zinc-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 focus-visible:ring-offset-2"
            >
              {copy.reject}
            </button>
          </div>
        </section>
      )}
    </>
  );
}
